import React, { useState, useEffect, useRef } from 'react';
import { Box, InputBase, List, ListItem, ListItemIcon, ListItemText, Paper, Typography, Fade } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import './MathCommandPalette.css';

interface MathSymbol {
    icon: string;
    name: string;
    latex: string;
}

const symbols: MathSymbol[] = [
    { icon: "-", name: "MINUS", latex: "-" },
    { icon: "·", name: "CENTER DOT", latex: "\\cdot" },
    { icon: "×", name: "TIMES", latex: "\\times" },
    { icon: "÷", name: "DIVISION", latex: "\\div" },
    { icon: "<", name: "LESS THAN", latex: "<" },
    { icon: ">", name: "GREATER THAN", latex: ">" },
    { icon: "□/□", name: "FRACTION", latex: "\\frac{#?}{#?}" },
    { icon: "√", name: "SQUARE ROOT", latex: "\\sqrt{#?}" },
    { icon: "π", name: "PI", latex: "\\pi" },
    { icon: "∞", name: "INFINITY", latex: "\\infty" },
    { icon: "∑", name: "SUMMATION", latex: "\\sum_{#?}^{#?}" },
    { icon: "∫", name: "INTEGRAL", latex: "\\int_{#?}^{#?}" },
];

interface MathCommandPaletteProps {
    onSelect: (latex: string, type: 'symbol' | 'template') => void;
}

const MathCommandPalette: React.FC<MathCommandPaletteProps> = ({ onSelect }) => {
    const [search, setSearch] = useState("");
    const [selectedIndex, setSelectedIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);

    const filteredSymbols = symbols.filter(s =>
        s.name.toLowerCase().includes(search.toLowerCase())
    );

    useEffect(() => {
        setSelectedIndex(0);
    }, [search]);

    const handleSelect = (symbol: MathSymbol) => {
        const isTemplate = symbol.latex.includes("{}");
        onSelect(symbol.latex, isTemplate ? 'template' : 'symbol');
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
            setSelectedIndex(prev => (prev + 1) % filteredSymbols.length);
            e.preventDefault();
        } else if (e.key === 'ArrowUp') {
            setSelectedIndex(prev => (prev - 1 + filteredSymbols.length) % filteredSymbols.length);
            e.preventDefault();
        } else if (e.key === 'Enter') {
            if (filteredSymbols.length > 0) {
                handleSelect(filteredSymbols[selectedIndex]);
            }
            e.preventDefault();
        }
    };

    return (
        <Box className="math-palette-container">
            <Fade in={true} timeout={400}>
                <Paper className="math-palette-paper" elevation={24}>
                    <Box className="search-container">
                        <SearchIcon className="search-icon" />
                        <InputBase
                            autoFocus
                            fullWidth
                            placeholder="Search symbols..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={handleKeyDown}
                            className="search-input"
                            inputRef={inputRef}
                        />
                    </Box>

                    <Box className="navigation-hint">
                        <Typography variant="caption">Use ↑↓ to navigate</Typography>
                    </Box>

                    <List className="symbols-list">
                        {filteredSymbols.map((symbol, index) => (
                            <ListItem
                                key={symbol.name}
                                className={`symbol-item ${index === selectedIndex ? 'selected' : ''}`}
                                onMouseEnter={() => setSelectedIndex(index)}
                                onClick={() => handleSelect(symbol)}
                            >
                                <ListItemIcon className="symbol-icon-container">
                                    <span className="symbol-preview">{symbol.icon}</span>
                                </ListItemIcon>
                                <ListItemText
                                    primary={symbol.name}
                                    className="symbol-text"
                                    primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            </Fade>
        </Box>
    );
};

export default MathCommandPalette;
